import type { CustomLevelIconName } from "../../../../icons/custom-level-icon-names";

export const TOEIC_LEVEL_IDS = ["basic", "intermediate", "proficient"] as const;

export type ToeicLevelId = (typeof TOEIC_LEVEL_IDS)[number];

export const TOEIC_LEVELS: {
	id: ToeicLevelId;
	icon: CustomLevelIconName;
	scoreMin: number;
	scoreMax: number;
	exampleKeys: readonly string[];
}[] = [
	{
		id: "basic",
		icon: "toeic-basic",
		scoreMin: 0,
		scoreMax: 12,
		exampleKeys: ["forms", "instructions", "announcements"],
	},
	{
		id: "intermediate",
		icon: "toeic-intermediate",
		scoreMin: 13,
		scoreMax: 21,
		exampleKeys: ["emails", "customers", "followUps"],
	},
	{
		id: "proficient",
		icon: "toeic-advanced",
		scoreMin: 22,
		scoreMax: 25,
		exampleKeys: ["reports", "negotiations", "highStakes"],
	},
];

export const TOEIC_PROGRESSION_STEP_KEYS = [
	"practiceSessions",
	"buildMastery",
	"growToward",
] as const;

export const TOEIC_LEVEL_OVERVIEW_I18N_ROOT =
	"onboarding.steps.toeicLevelOverview";

export const toeicLevelOverviewKey = (suffix: string) =>
	`${TOEIC_LEVEL_OVERVIEW_I18N_ROOT}.${suffix}`;

/**
 * Illustration geometry.
 *
 * Ratios are measured from the source Lottie's held state in its 1271x651
 * artboard (ring 259, stroke 18, icon 73.7, gap 107, connector 123x18) and
 * expressed relative to the ring so the illustration stays proportional if
 * RING_DIAMETER changes. See docs/plans/toeic-level-overview-onboarding-step.
 */
export const RING_DIAMETER = 76;

const RING_STROKE_RATIO = 18 / 259;
const ICON_RATIO = 73.7 / 259;
const GAP_RATIO = 107 / 259;
const CONNECTOR_LENGTH_RATIO = 123 / 259;

export const RING_STROKE_WIDTH = RING_DIAMETER * RING_STROKE_RATIO;
export const RING_ICON_SIZE = RING_DIAMETER * ICON_RATIO;
export const RING_GAP = RING_DIAMETER * GAP_RATIO;
export const CONNECTOR_LENGTH = RING_DIAMETER * CONNECTOR_LENGTH_RATIO;
export const CONNECTOR_THICKNESS = RING_STROKE_WIDTH;

/**
 * The connector is longer than the gap, so it tucks under each ring — matching
 * the Lottie, where connectors are drawn behind the rings.
 */
export const CONNECTOR_OVERLAP = (CONNECTOR_LENGTH - RING_GAP) / 2;

export const ILLUSTRATION_ROW_WIDTH = RING_DIAMETER * 3 + RING_GAP * 2;

/**
 * Draw-in timing. Staggered ring → connector → ring → connector → ring totals
 * ~1.84s, compressing the Lottie's ~3s build (frames 0–180 @ 60fps).
 */
export const RING_DRAW_DURATION = 460;
export const CONNECTOR_DRAW_DURATION = 230;

/**
 * Grace period after the draw-in should have finished, before the illustration
 * is forced to its end state. Covers hosts that do not drive JS-based Animated.
 */
export const WATCHDOG_GRACE_MS = 400;

export type DrawSegment = { start: number; end: number };

export type DrawTimeline = {
	duration: number;
	rings: DrawSegment[];
	connectors: DrawSegment[];
};

/**
 * Splits the draw-in into normalized 0–1 segments, one per ring and connector,
 * laid out as ring → connector → ring → connector → ring.
 *
 * A single Animated.Value runs 0 → 1 and each element interpolates its own
 * segment out of it. The earlier implementation chained one timing per element
 * with Animated.sequence, where a single interrupted callback silently killed
 * every animation after it — leaving rings 2 and 3 invisible forever. One
 * driver has no such failure mode.
 */
export const buildDrawTimeline = (ringCount: number): DrawTimeline => {
	const connectorCount = Math.max(ringCount - 1, 0);
	const duration =
		ringCount * RING_DRAW_DURATION + connectorCount * CONNECTOR_DRAW_DURATION;

	const rings: DrawSegment[] = [];
	const connectors: DrawSegment[] = [];

	if (duration === 0) {
		return { duration, rings, connectors };
	}

	let elapsed = 0;
	for (let index = 0; index < ringCount; index++) {
		rings.push({
			start: elapsed / duration,
			end: (elapsed + RING_DRAW_DURATION) / duration,
		});
		elapsed += RING_DRAW_DURATION;

		if (index < connectorCount) {
			connectors.push({
				start: elapsed / duration,
				end: (elapsed + CONNECTOR_DRAW_DURATION) / duration,
			});
			elapsed += CONNECTOR_DRAW_DURATION;
		}
	}

	return { duration, rings, connectors };
};
